'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { X, Search, Receipt, Clock } from 'lucide-react';
import { pastOrders } from '../data/mock-data';
import type { POSOrder, POSOrderStatus } from '../types';
import { ORDER_STATUS_LABELS } from '../types';
import { formatCurrency } from '../../../types';

interface PastOrderListProps {
  onClose: () => void;
  onSelectOrder?: (order: POSOrder) => void;
}

export function PastOrderList({ onClose, onSelectOrder }: PastOrderListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<POSOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<POSOrderStatus | 'all'>('all');

  const filteredOrders = pastOrders.filter((order) => {
    const matchesSearch =
      order.orderNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* 주문 목록 패널 */}
      <div className="relative ml-auto w-full max-w-md bg-card h-full flex flex-col shadow-xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            <h2 className="text-lg font-semibold">주문 내역</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-accent">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 검색 및 필터 */}
        <div className="p-4 border-b border-border space-y-3">
          {/* 검색 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="주문번호, 고객명 검색..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* 상태 필터 */}
          <div className="flex gap-2 overflow-x-auto">
            <FilterButton
              label="전체"
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            />
            <FilterButton
              label="결제완료"
              active={statusFilter === 'paid'}
              onClick={() => setStatusFilter('paid')}
            />
            <FilterButton
              label="취소"
              active={statusFilter === 'cancelled'}
              onClick={() => setStatusFilter('cancelled')}
            />
            <FilterButton
              label="환불"
              active={statusFilter === 'refunded'}
              onClick={() => setStatusFilter('refunded')}
            />
          </div>
        </div>

        {/* 주문 목록 */}
        <div className="flex-1 overflow-y-auto">
          {filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Receipt className="w-12 h-12 mb-2 opacity-50" />
              <p>주문 내역이 없습니다</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredOrders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  selected={selectedOrder?.id === order.id}
                  onClick={() => setSelectedOrder(order)}
                />
              ))}
            </div>
          )}
        </div>

        {/* 선택된 주문 상세 */}
        {selectedOrder && (
          <OrderDetail
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </div>
  );
}

function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-accent'
      )}
    >
      {label}
    </button>
  );
}

function OrderRow({
  order,
  selected,
  onClick,
}: {
  order: POSOrder;
  selected: boolean;
  onClick: () => void;
}) {
  const statusColors: Record<POSOrderStatus, string> = {
    draft: 'bg-gray-500',
    paid: 'bg-green-500',
    cancelled: 'bg-red-500',
    refunded: 'bg-yellow-500',
  };

  const date = new Date(order.createdAt);
  const timeStr = date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const dateStr = date.toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-4 text-left hover:bg-accent transition-colors',
        selected && 'bg-accent'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">{order.orderNo}</p>
          {order.customer && (
            <p className="text-sm text-muted-foreground">{order.customer}</p>
          )}
        </div>
        <div className="text-right">
          <p className="font-semibold">{formatCurrency(order.total)}</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {dateStr} {timeStr}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-xs text-white',
            statusColors[order.status]
          )}
        >
          {ORDER_STATUS_LABELS[order.status]}
        </span>
        <span className="text-xs text-muted-foreground">
          {order.items.length}개 품목
        </span>
      </div>
    </button>
  );
}

function OrderDetail({
  order,
  onClose,
}: {
  order: POSOrder;
  onClose: () => void;
}) {
  return (
    <div className="absolute inset-0 bg-card flex flex-col">
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold">{order.orderNo}</h3>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-accent">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* 주문 정보 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 기본 정보 */}
        <div className="space-y-2">
          <InfoRow label="주문번호" value={order.orderNo} />
          <InfoRow
            label="주문일시"
            value={new Date(order.createdAt).toLocaleString('ko-KR')}
          />
          {order.customer && <InfoRow label="고객" value={order.customer} />}
          <InfoRow label="상태" value={ORDER_STATUS_LABELS[order.status]} />
        </div>

        {/* 주문 품목 */}
        <div>
          <h4 className="font-medium mb-2">주문 품목</h4>
          <div className="space-y-2">
            {order.items.map((item, index) => (
              <div
                key={index}
                className="flex justify-between text-sm p-2 bg-muted rounded-lg"
              >
                <div>
                  <p className="font-medium">{item.item.name}</p>
                  <p className="text-muted-foreground">
                    {formatCurrency(item.item.price)} × {item.quantity}
                    {item.discount > 0 && ` (-${item.discount}%)`}
                  </p>
                </div>
                <p className="font-medium">
                  {formatCurrency(
                    item.item.price * item.quantity * (1 - item.discount / 100)
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 금액 요약 */}
        <div className="border-t border-border pt-4 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">소계</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between text-sm text-green-500">
              <span>할인</span>
              <span>-{formatCurrency(order.discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">부가세</span>
            <span>{formatCurrency(order.tax)}</span>
          </div>
          <div className="flex justify-between font-bold pt-2 border-t border-border">
            <span>합계</span>
            <span className="text-primary">{formatCurrency(order.total)}</span>
          </div>
        </div>

        {/* 결제 정보 */}
        {order.payments.length > 0 && (
          <div className="border-t border-border pt-4 space-y-1">
            <h4 className="font-medium mb-2">결제 정보</h4>
            {order.payments.map((payment, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {payment.method === 'cash' && '현금'}
                  {payment.method === 'card' && '카드'}
                  {payment.method === 'transfer' && '계좌이체'}
                </span>
                <span>{formatCurrency(payment.amount)}</span>
              </div>
            ))}
            {order.change > 0 && (
              <div className="flex justify-between text-sm text-green-500">
                <span>거스름돈</span>
                <span>{formatCurrency(order.change)}</span>
              </div>
            )}
          </div>
        )}

        {/* 메모 */}
        {order.note && (
          <div className="border-t border-border pt-4">
            <h4 className="font-medium mb-2">메모</h4>
            <p className="text-sm text-muted-foreground">{order.note}</p>
          </div>
        )}
      </div>

      {/* 버튼 */}
      <div className="p-4 border-t border-border flex gap-2">
        <button className="flex-1 py-2 rounded-lg border border-border hover:bg-accent">
          영수증 인쇄
        </button>
        {order.status === 'paid' && (
          <button className="flex-1 py-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20">
            환불
          </button>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
